import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const emptyJob = { role: "", company: "", period: "", details: "" };

export default function AdminCV() {
  const [cv, setCv] = useState(null);
  const [skillsText, setSkillsText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cv")
      .then((data) => { setCv(data); setSkillsText(data.skills.join("\n")); })
      .catch(() => toast.error("Couldn't load CV content."));
  }, []);

  const setJob = (i, field) => (e) => {
    const experience = cv.experience.map((job, idx) => (idx === i ? { ...job, [field]: e.target.value } : job));
    setCv({ ...cv, experience });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      intro: cv.intro.trim(),
      experience: cv.experience.map((j) => ({ role: j.role.trim(), company: j.company.trim(), period: j.period.trim(), details: j.details.trim() })),
      skills: skillsText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.put("/cv", body);
      toast.success("CV updated.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!cv) return <p className="text-stone-500">Loading...</p>;

  return (
    <form onSubmit={save} className="space-y-10" data-testid="admin-cv-form">
      <div className="space-y-2">
        <Label htmlFor="cv-intro">Intro line</Label>
        <Textarea id="cv-intro" required rows={2} value={cv.intro} onChange={(e) => setCv({ ...cv, intro: e.target.value })} data-testid="cv-intro-input" className="bg-white" />
      </div>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg md:text-lg font-bold text-stone-900">Experience</h2>
          <Button type="button" variant="outline" onClick={() => setCv({ ...cv, experience: [...cv.experience, { ...emptyJob }] })} data-testid="cv-add-experience-button" className="rounded-full gap-2">
            <Plus className="h-4 w-4" /> Add role
          </Button>
        </div>
        <div className="mt-4 space-y-4">
          {cv.experience.map((job, i) => (
            <div key={i} className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm space-y-3" data-testid={`cv-experience-editor-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input required placeholder="Role" value={job.role} onChange={setJob(i, "role")} data-testid={`cv-role-input-${i}`} />
                <Input required placeholder="Company" value={job.company} onChange={setJob(i, "company")} data-testid={`cv-company-input-${i}`} />
                <Input required placeholder="Period (e.g. 2022 – Present)" value={job.period} onChange={setJob(i, "period")} data-testid={`cv-period-input-${i}`} />
              </div>
              <Textarea required rows={3} placeholder="What you did and achieved" value={job.details} onChange={setJob(i, "details")} data-testid={`cv-details-input-${i}`} />
              <button type="button" onClick={() => setCv({ ...cv, experience: cv.experience.filter((_, idx) => idx !== i) })} data-testid={`cv-remove-experience-${i}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-800">
                <Trash2 className="h-3.5 w-3.5" /> Remove role
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="space-y-2">
        <Label htmlFor="cv-skills">Skills (one per line)</Label>
        <Textarea id="cv-skills" rows={6} value={skillsText} onChange={(e) => setSkillsText(e.target.value)} data-testid="cv-skills-input" className="bg-white" />
      </div>

      <Button type="submit" disabled={saving} data-testid="cv-save-button" className="rounded-full bg-amber-600 px-8 font-semibold text-white hover:bg-amber-700 gap-2">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save CV"}
      </Button>
    </form>
  );
}
